/**
 * Per-domain verification + SSL status workflow.
 * Reads `tenant_domain_verifications`, exposes a "Verify" action, and
 * renders DNS guidance with copyable records.
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { upsertTenantBranding } from "@/lib/tenant-branding";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ShieldCheck, ShieldAlert, Loader2, Copy, RefreshCw, Trash2, Plus,
  Globe, AlertTriangle, CheckCircle2, Timer,
} from "lucide-react";

type Verification = {
  id: string;
  tenant_slug: string;
  host: string;
  status: "pending" | "verifying" | "verified" | "failed" | "offline";
  dns_target: string;
  verification_token: string;
  ssl_status: "pending" | "issuing" | "active" | "failed";
  ssl_issued_at: string | null;
  last_checked_at: string | null;
  last_error: string | null;
};

export function DomainVerificationPanel({
  slug,
  primaryDomain,
  customDomains,
  onCustomDomainsChange,
}: {
  slug: string;
  primaryDomain: string;
  customDomains: string[];
  onCustomDomainsChange: (v: string[]) => void;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState("");

  const { data: verifications = [], refetch, isLoading } = useQuery({
    queryKey: ["tdv", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tenant_domain_verifications" as never)
        .select("*")
        .eq("tenant_slug", slug)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data as Verification[]) ?? [];
    },
  });

  // Union of primary domain, saved custom_domains, and any host that already
  // has a verification row for this tenant — so previously-added domains keep
  // showing even when the parent branding row hasn't been re-saved.
  const allHosts = useMemo(() => {
    const set = new Set<string>();
    if (primaryDomain) set.add(primaryDomain.toLowerCase());
    for (const d of customDomains ?? []) if (d) set.add(d.toLowerCase());
    for (const v of verifications) if (v.host) set.add(v.host.toLowerCase());
    return [...set];
  }, [primaryDomain, customDomains, verifications]);

  const byHost = useMemo(
    () => new Map(verifications.map((v) => [v.host.toLowerCase(), v])),
    [verifications],
  );

  // Reconcile: if verification rows exist for hosts not yet in custom_domains
  // (and not the primary), surface them into the parent list so they persist.
  useEffect(() => {
    if (!verifications.length) return;
    const known = new Set<string>([
      (primaryDomain || "").toLowerCase(),
      ...(customDomains ?? []).map((d) => d.toLowerCase()),
    ]);
    const missing = verifications
      .map((v) => v.host.toLowerCase())
      .filter((h) => h && !known.has(h));
    if (missing.length) onCustomDomainsChange([...(customDomains ?? []), ...missing]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifications]);

  const persistCustomDomains = async (next: string[]) => {
    try {
      await upsertTenantBranding({ slug, custom_domains: next });
      qc.invalidateQueries({ queryKey: ["tenant_brandings"] });
    } catch (e) {
      toast.error("Could not save domain list", { description: (e as Error).message });
    }
  };

  const verify = async (host: string) => {
    const toastId = toast.loading(`Verifying ${host}…`);
    try {
      const { error } = await supabase.rpc("verify_tenant_domain" as never, { _slug: slug, _host: host } as never);
      if (error) throw error;
      await qc.invalidateQueries({ queryKey: ["tdv", slug] });
      toast.success(`${host} rechecked`, { id: toastId });
    } catch (e) {
      toast.error("Verify failed", { id: toastId, description: (e as Error).message });
    }
  };

  const remove = async (host: string) => {
    const next = (customDomains ?? []).filter((d) => d.toLowerCase() !== host.toLowerCase());
    onCustomDomainsChange(next);
    await persistCustomDomains(next);
    try {
      await supabase.from("tenant_domain_verifications" as never).delete().eq("host", host);
      qc.invalidateQueries({ queryKey: ["tdv", slug] });
    } catch { /* ignore */ }
    toast.success(`Removed ${host}`);
  };

  const add = async () => {
    const clean = draft.trim().toLowerCase();
    if (!/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(clean)) return toast.error("Enter a valid hostname");
    if ((customDomains ?? []).includes(clean)) return;
    const next = [...(customDomains ?? []), clean];
    onCustomDomainsChange(next);
    setDraft("");
    await persistCustomDomains(next);
    void verify(clean);
  };

  const failed = verifications.filter((v) => v.status === "failed" || v.ssl_status === "failed");
  const anyPending = verifications.some((v) => v.status === "verifying" || v.ssl_status === "issuing");

  return (
    <div className="space-y-4">
      {/* Error / status banner */}
      {failed.length > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 text-destructive" />
          <div className="flex-1">
            <div className="font-medium text-destructive">
              {failed.length} domain{failed.length > 1 ? "s" : ""} need attention
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              Requests to these hosts automatically fall back to <code className="rounded bg-background px-1">{primaryDomain || "tricampus.live"}</code> until DNS is fixed.
            </div>
            <ul className="mt-1 space-y-0.5 text-xs">
              {failed.map((v) => (
                <li key={v.host} className="font-mono">
                  {v.host}: {v.last_error || "verification failed"}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {anyPending && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          <Timer className="h-3.5 w-3.5 animate-pulse" />
          Provisioning in progress — SSL certificates typically issue in under 5 minutes after DNS propagates.
        </div>
      )}

      {/* Add domain */}
      <div className="flex gap-2">
        <Input
          value={draft}
          placeholder="portal.myschool.ac.ug"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
        />
        <Button size="sm" variant="outline" onClick={add}>
          <Plus className="mr-1 h-3 w-3" />Add & verify
        </Button>
        <Button size="sm" variant="ghost" onClick={() => refetch()} disabled={isLoading}>
          <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {/* Per-domain cards */}
      <div className="space-y-2">
        {allHosts.length === 0 && (
          <div className="rounded-md border border-dashed p-6 text-center text-xs text-muted-foreground">
            No domains configured yet.
          </div>
        )}
        {allHosts.map((host) => {
          const v = byHost.get(host);
          const isPrimary = host === (primaryDomain || "").toLowerCase();
          return (
            <Card key={host} className="p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Globe className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="truncate font-mono text-sm font-medium">{host}</span>
                    {isPrimary && <Badge variant="secondary" className="text-[10px]">primary</Badge>}
                    <StatusBadge status={v?.status ?? "pending"} />
                    <SslBadge status={v?.ssl_status ?? "pending"} />
                  </div>
                  {v?.last_checked_at && (
                    <div className="mt-0.5 text-[10px] text-muted-foreground">
                      Last checked {new Date(v.last_checked_at).toLocaleString()}
                      {v.ssl_issued_at && ` · SSL since ${new Date(v.ssl_issued_at).toLocaleDateString()}`}
                    </div>
                  )}
                  {v?.last_error && (
                    <div className="mt-1 rounded border border-destructive/30 bg-destructive/5 px-2 py-1 text-[11px] text-destructive">
                      {v.last_error}
                    </div>
                  )}
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button size="sm" variant="outline" onClick={() => verify(host)}>
                    <ShieldCheck className="mr-1 h-3 w-3" />Verify
                  </Button>
                  {!isPrimary && (
                    <Button size="sm" variant="ghost" onClick={() => remove(host)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>

              {/* DNS guidance */}
              {(v?.status !== "verified" || v?.ssl_status !== "active") && (
                <div className="mt-3 space-y-1.5 rounded-md border bg-muted/40 p-3 text-[11px]">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Add these records at your DNS provider</div>
                  <DnsRow type="CNAME" name={host} value={v?.dns_target ?? "tricampus.live"} />
                  {v?.verification_token && (
                    <DnsRow type="TXT" name={`_tricampus.${host}`} value={v.verification_token} />
                  )}
                  <div className="pt-1 text-[10px] text-muted-foreground">
                    Propagation typically takes 15 minutes – 2 hours. Certificates auto-issue after verification.
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function DnsRow({ type, name, value }: { type: string; name: string; value: string }) {
  return (
    <div className="grid grid-cols-[60px_1fr_1fr_auto] items-center gap-2 rounded bg-background/60 px-2 py-1">
      <Badge variant="outline" className="justify-center text-[10px]">{type}</Badge>
      <code className="truncate text-[11px]">{name}</code>
      <code className="truncate text-[11px] text-primary">{value}</code>
      <Button
        size="sm"
        variant="ghost"
        className="h-6 w-6 p-0"
        onClick={() => { void navigator.clipboard.writeText(value); toast.success("Copied"); }}
      >
        <Copy className="h-3 w-3" />
      </Button>
    </div>
  );
}

function StatusBadge({ status }: { status: Verification["status"] }) {
  const map: Record<string, { cls: string; label: string; icon: any }> = {
    pending: { cls: "bg-muted text-muted-foreground border-0", label: "pending", icon: Timer },
    verifying: { cls: "bg-amber-500/15 text-amber-700 border-0", label: "verifying", icon: Loader2 },
    verified: { cls: "bg-emerald-500/15 text-emerald-700 border-0", label: "verified", icon: CheckCircle2 },
    failed: { cls: "bg-destructive/15 text-destructive border-0", label: "failed", icon: ShieldAlert },
    offline: { cls: "bg-slate-500/15 text-slate-700 border-0", label: "offline", icon: ShieldAlert },
  };
  const cfg = map[status] ?? map.pending;
  const Icon = cfg.icon;
  return (
    <Badge className={`gap-1 text-[10px] ${cfg.cls}`}>
      <Icon className={`h-2.5 w-2.5 ${status === "verifying" ? "animate-spin" : ""}`} />
      {cfg.label}
    </Badge>
  );
}

function SslBadge({ status }: { status: Verification["ssl_status"] }) {
  const map: Record<string, { cls: string; label: string }> = {
    pending: { cls: "bg-muted text-muted-foreground border-0", label: "SSL pending" },
    issuing: { cls: "bg-amber-500/15 text-amber-700 border-0", label: "SSL issuing" },
    active: { cls: "bg-emerald-500/15 text-emerald-700 border-0", label: "SSL active" },
    failed: { cls: "bg-destructive/15 text-destructive border-0", label: "SSL failed" },
  };
  const cfg = map[status] ?? map.pending;
  return <Badge className={`text-[10px] ${cfg.cls}`}>{cfg.label}</Badge>;
}
